import { useCallback, useEffect, useState } from "react";
import { toast, useToast } from "./use-toast";
import { useParams, useRouter } from "next/navigation";
import { Template, TemplateAPI } from "@/lib/template-api";
import { apiFetch } from "@/lib/api/apiFetch";

/* -------------------------------------------------------------------------- */
/*                               useDebounce Hook                              */
/* -------------------------------------------------------------------------- */

/**
 * useDebounce
 * -----------
 * Generic debounce hook that delays updating the returned value
 * until after a specified delay has passed without changes.
 *
 * Commonly used to optimize search inputs and prevent excessive API calls.
 *
 * @template T
 * @param {T} value - The value to debounce
 * @param {number} delay - Delay in milliseconds
 * @returns {T} Debounced value
 */
function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState(value)

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value)
        }, delay)

        return () => clearTimeout(handler)
    }, [value, delay])

    return debouncedValue
}

/* -------------------------------------------------------------------------- */
/*                             useTemplatesHook                                */
/* -------------------------------------------------------------------------- */

/**
 * useTemplatesHook
 * ================
 * Centralized hook for managing email templates.
 *
 * Responsibilities:
 * - Fetch templates with pagination, category & search filters
 * - Debounced searching
 * - Archive / restore templates
 * - Duplicate templates
 * - Pagination & category handling
 * - Centralized loading, error, and toast handling
 *
 * @returns {{
 *  confirmArchiveTemplate: () => Promise<void>,
 *  fetchTemplates: () => Promise<void>,
 *  handleSearch: () => Promise<void>,
 *  handleSearchChange: (value: string) => void,
 *  handleArchiveToggle: (templateId: string, isArchived: boolean) => Promise<void>,
 *  handleDuplicate: (templateId: string) => Promise<void>,
 *  handlePageChange: (page: number) => void,
 *  searchTimeout: NodeJS.Timeout | null,
 *  searchTerm: string,
 *  templates: Template[],
 *  loading: boolean,
 *  error: string | null,
 *  selectedCategory: string,
 *  setSearchTerm: (value: string) => void,
 *  setSelectedCategory: (value: string) => void,
 *  setCurrentPage: (value: number) => void,
 *  processingIds: Set<string>,
 *  handleArchiveTemplate: (templateId: string) => Promise<void>,
 *  archivingTemplates: Set<string>,
 *  pagination: {
 *    page: number,
 *    limit: number,
 *    total: number,
 *    pages: number
 *  },
 *  currentPage: number,
 *  showArchiveDialog: boolean,
 *  setShowArchiveDialog: (value: boolean) => void,
 *  templateToArchive: Template | null
 * }}
 */
export const useTemplatesHook = () => {
    /* ------------------------------ State ---------------------------------- */

    /** List of templates displayed in the UI */
    const [templates, setTemplates] = useState<Template[]>([])

    /** Global loading state */
    const [loading, setLoading] = useState(true)

    /** Error message from API failures */
    const [error, setError] = useState<string | null>(null)

    /** Search input value */
    const [searchTerm, setSearchTerm] = useState("")

    /** Currently selected template category */
    const [selectedCategory, setSelectedCategory] = useState<string>("all")

    /** Current page number */
    const [currentPage, setCurrentPage] = useState(1)

    /**
     * Pagination metadata returned from API
     */
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0,
    })

    /**
     * IDs of templates currently being processed
     * (archive, restore, duplicate)
     */
    const [processingIds, setProcessingIds] = useState<Set<string>>(new Set())

    /**
     * Timeout reference for manual debounced search
     */
    const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null)

    /** Controls archive confirmation dialog */
    const [showArchiveDialog, setShowArchiveDialog] = useState(false)

    /** IDs of templates currently being archived */
    const [archivingTemplates, setArchivingTemplates] = useState<Set<string>>(new Set())

    /** Template selected for archive confirmation */
    const [templateToArchive, setTemplateToArchive] = useState<Template | null>(null)

    const { toast } = useToast()

    /* ------------------------------ Archive -------------------------------- */

    /**
     * Opens archive confirmation dialog for a template
     *
     * @param {string} templateId - Template ID
     * @returns {Promise<void>}
     */
    const handleArchiveTemplate = async (templateId: string) => {
        setTemplateToArchive(templates.find(c => c.id === templateId) || null)
        setShowArchiveDialog(true)
    }

    /* ------------------------------ Fetching -------------------------------- */

    /**
     * Debounced search term to reduce API calls
     */
    const debouncedSearchTerm = useDebounce(searchTerm, 500)

    /**
     * Fetch templates from API with pagination, category & search
     *
     * Side effects:
     * - Updates `templates`
     * - Updates `pagination`
     * - Toggles loading and error state
     *
     * @returns {Promise<void>}
     */
    const fetchTemplates = useCallback(async () => {
        try {
            setLoading(true)
            setError(null)

            const response = await TemplateAPI.getTemplates({
                page: currentPage,
                limit: 20,
                category: selectedCategory === "all" ? undefined : selectedCategory,
                search: debouncedSearchTerm || "",
            })

            setTemplates(response.templates)
            setPagination(response.pagination)
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to fetch templates")
            toast({
                title: "Error",
                description: "Failed to fetch templates",
                variant: "destructive",
            })
        } finally {
            setLoading(false)
        }
    }, [currentPage, selectedCategory, debouncedSearchTerm, toast])

    /* ------------------------------ Archive Confirm -------------------------- */

    /**
     * Confirms and archives selected template
     *
     * Side effects:
     * - Sends archive API request
     * - Removes template from local list
     * - Updates archive dialog state
     *
     * @returns {Promise<void>}
     */
    const confirmArchiveTemplate = async () => {
        if (!templateToArchive) return

        try {
            setArchivingTemplates(prev => new Set(prev).add(templateToArchive.id))

            await apiFetch(`/api/templates/${templateToArchive.id}/manage`, {
                method: "PUT",
                data: { action: "archive" },
            })

            toast({
                title: "Success",
                description: "Template archived successfully",
            })

            setTemplates(prev => prev.filter(t => t.id !== templateToArchive.id))
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to archive contact",
                variant: "destructive",
            })
        } finally {
            setArchivingTemplates(prev => {
                const newSet = new Set(prev)
                newSet.delete(templateToArchive.id)
                return newSet
            })
            setShowArchiveDialog(false)
            setTemplateToArchive(null)
        }
    }

    /* ------------------------------ Search ---------------------------------- */

    /**
     * Triggers search and resets pagination
     *
     * @returns {Promise<void>}
     */
    const handleSearch = useCallback(async () => {
        setCurrentPage(1)
        await fetchTemplates()
    }, [fetchTemplates])

    /**
     * Handles search input change with manual debounce
     *
     * Side effects:
     * - Updates search term
     * - Resets pagination
     * - Triggers template fetch after delay
     *
     * @param {string} value - Search input value
     * @returns {void}
     */
    const handleSearchChange = (value: string) => {
        setSearchTerm(value)

        if (searchTimeout) {
            clearTimeout(searchTimeout)
        }

        const timeout = setTimeout(() => {
            if (value !== searchTerm) {
                setCurrentPage(1)
                fetchTemplates()
            }
        }, 500)

        setSearchTimeout(timeout)
    }

    /* ------------------------------ Actions --------------------------------- */

    /**
     * Archives or restores a template
     *
     * @param {string} templateId - Template ID
     * @param {boolean} isArchived - Archive or restore flag
     * @returns {Promise<void>}
     */
    const handleArchiveToggle = async (templateId: string, isArchived: boolean) => {
        try {
            setProcessingIds(prev => new Set(prev).add(templateId))

            await TemplateAPI.archiveTemplate(templateId, isArchived)

            setTemplates(prev =>
                prev.map(template =>
                    template.id === templateId
                        ? { ...template, isArchived } as Template
                        : template
                )
            )

            toast({
                title: "Success",
                description: `Template ${isArchived ? "archived" : "restored"} successfully`,
            })
        } catch (err) {
            toast({
                title: "Error",
                description: err instanceof Error ? err.message : "Failed to update template",
                variant: "destructive",
            })
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(templateId)
                return newSet
            })
        }
    }

    /**
     * Duplicates a template
     *
     * @param {string} templateId - Template ID
     * @returns {Promise<void>}
     */
    const handleDuplicate = async (templateId: string) => {
        try {
            setProcessingIds(prev => new Set(prev).add(templateId))

            const duplicatedTemplate = await TemplateAPI.duplicateTemplate(templateId)

            setTemplates(prev => [duplicatedTemplate, ...prev])

            toast({
                title: "Success",
                description: "Template duplicated successfully",
            })
        } catch (err) {
            toast({
                title: "Error",
                description: err instanceof Error ? err.message : "Failed to duplicate template",
                variant: "destructive",
            })
        } finally {
            setProcessingIds(prev => {
                const newSet = new Set(prev)
                newSet.delete(templateId)
                return newSet
            })
        }
    }

    /* ------------------------------ Pagination ------------------------------ */

    /**
     * Updates current page
     *
     * @param {number} page - Page number
     * @returns {void}
     */
    const handlePageChange = (page: number) => {
        setCurrentPage(page)
    }

    /**
     * Handles category change and resets pagination
     *
     * @param {string} category - Selected category
     * @returns {void}
     */
    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category)
        setCurrentPage(1)
    }

    /* ------------------------------ RETURN ---------------------------------- */

    return {
        confirmArchiveTemplate,
        fetchTemplates,
        handleSearch,
        handleSearchChange,
        handleArchiveToggle,
        handleDuplicate,
        handlePageChange,
        searchTimeout,
        searchTerm,
        templates,
        loading,
        error,
        selectedCategory,
        setSearchTerm,
        setSelectedCategory,
        setCurrentPage,
        processingIds,
        handleArchiveTemplate,
        archivingTemplates,
        pagination,
        currentPage,
        showArchiveDialog,
        setShowArchiveDialog,
        templateToArchive
    };
};
